/**
 * Clinic Admin Document Management - Phase 2E
 * Admin interface for managing legal documents
 * P2.3b: Full i18n coverage
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocuments } from '../../../context/DocumentContext';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { 
  FileText, Plus, Upload, Eye, Trash2, History, X,
  CheckCircle, AlertCircle, Settings, Download, Edit
} from 'lucide-react';
import { toast } from '../../../hooks/use-toast';

const DocumentManagement = ({ clinicId }) => {
  const { t } = useTranslation();
  const {
    getDocumentsForClinic,
    addDocument,
    uploadNewVersion,
    updateDocument,
    deleteDocument
  } = useDocuments();

  const documents = getDocumentsForClinic(clinicId);
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  const [newDocument, setNewDocument] = useState({
    title: '',
    description: '',
    category: 'consent',
    isRequired: true,
    requiresSignature: true
  });

  const [versionNotes, setVersionNotes] = useState('');

  const categories = [
    { value: 'consent', labelKey: 'provider.documents.categories.consent' },
    { value: 'medical', labelKey: 'provider.documents.categories.medical' },
    { value: 'legal', labelKey: 'provider.documents.categories.legal' },
    { value: 'financial', labelKey: 'provider.documents.categories.financial' },
    { value: 'instructions', labelKey: 'provider.documents.categories.instructions' }
  ];

  const handleAddDocument = () => {
    if (!newDocument.title.trim()) {
      toast({ title: t('provider.documents.toasts.errorTitle'), description: t('provider.documents.toasts.titleRequired'), variant: 'destructive' });
      return;
    }
    
    addDocument(clinicId, newDocument);
    toast({ title: t('provider.documents.toasts.addedTitle'), description: t('provider.documents.toasts.addedDesc') });
    setShowAddModal(false);
    setNewDocument({
      title: '',
      description: '',
      category: 'consent',
      isRequired: true,
      requiresSignature: true
    });
  };

  const handleUploadVersion = () => {
    if (!selectedDocument) return;
    
    uploadNewVersion(selectedDocument.id, { notes: versionNotes });
    toast({ title: t('provider.documents.toasts.versionUploadedTitle'), description: t('provider.documents.toasts.versionUploadedDesc') });
    setShowVersionModal(false);
    setVersionNotes('');
  };

  const handleToggleRequired = (doc) => {
    updateDocument(doc.id, { isRequired: !doc.isRequired });
    toast({ 
      title: doc.isRequired ? t('provider.documents.toasts.markedOptional') : t('provider.documents.toasts.markedRequired'),
      description: t('provider.documents.toasts.updatedDesc', { title: doc.title })
    });
  };

  const handleDelete = (doc) => {
    if (window.confirm(t('provider.documents.toasts.deleteConfirm', { title: doc.title }))) {
      deleteDocument(doc.id);
      toast({ title: t('provider.documents.toasts.deletedTitle'), description: t('provider.documents.toasts.deletedDesc') });
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'consent': return 'bg-blue-100 text-blue-700';
      case 'medical': return 'bg-green-100 text-green-700';
      case 'legal': return 'bg-purple-100 text-purple-700';
      case 'financial': return 'bg-amber-100 text-amber-700';
      case 'instructions': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('provider.documents.title')}</h2>
          <p className="text-gray-500">{t('provider.documents.subtitle')}</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('provider.documents.add')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{documents.length}</p>
              <p className="text-sm text-slate-500">{t('provider.documents.stats.total')}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {documents.filter(d => d.isRequired).length}
              </p>
              <p className="text-sm text-slate-500">{t('provider.documents.stats.required')}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {documents.filter(d => d.requiresSignature).length}
              </p>
              <p className="text-sm text-slate-500">{t('provider.documents.stats.needSignature')}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <History className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">
                {documents.reduce((sum, d) => sum + (d.versions?.length || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">{t('provider.documents.stats.totalVersions')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-600">{t('provider.documents.table.document')}</th>
                  <th className="text-left p-4 font-semibold text-slate-600">{t('provider.documents.table.category')}</th>
                  <th className="text-left p-4 font-semibold text-slate-600">{t('provider.documents.table.version')}</th>
                  <th className="text-left p-4 font-semibold text-slate-600">{t('provider.documents.table.status')}</th>
                  <th className="text-left p-4 font-semibold text-slate-600">{t('provider.documents.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => (
                  <tr key={doc.id} className="border-b hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="h-5 w-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{doc.title}</p>
                          <p className="text-sm text-slate-500 line-clamp-1">{doc.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={getCategoryColor(doc.category)}>
                        {t(categories.find(c => c.value === doc.category)?.labelKey || `provider.documents.categories.${doc.category}`, { defaultValue: doc.category })}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">v{doc.currentVersion}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setShowHistoryModal(true);
                          }}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        {doc.isRequired && (
                          <Badge className="bg-red-100 text-red-700 w-fit">
                            {t('provider.documents.badges.required')}
                          </Badge>
                        )}
                        {doc.requiresSignature && (
                          <Badge className="bg-blue-100 text-blue-700 w-fit">
                            {t('provider.documents.badges.signatureRequired')}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setShowPreviewModal(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedDocument(doc);
                            setShowVersionModal(true);
                          }}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleRequired(doc)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(doc)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Document Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('provider.documents.modals.addTitle')}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t('provider.documents.modals.titleLabel')}</Label>
                <Input
                  value={newDocument.title}
                  onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                  placeholder={t('provider.documents.modals.titlePlaceholder')}
                />
              </div>
              
              <div>
                <Label>{t('provider.documents.modals.descriptionLabel')}</Label>
                <textarea
                  className="w-full min-h-[80px] p-3 border rounded-lg resize-none"
                  value={newDocument.description}
                  onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                  placeholder={t('provider.documents.modals.descriptionPlaceholder')}
                />
              </div>
              
              <div>
                <Label>{t('provider.documents.modals.categoryLabel')}</Label>
                <select
                  className="w-full p-2 border rounded-lg"
                  value={newDocument.category}
                  onChange={(e) => setNewDocument({ ...newDocument, category: e.target.value })}
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{t(cat.labelKey)}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="isRequired"
                    checked={newDocument.isRequired}
                    onCheckedChange={(checked) => setNewDocument({ ...newDocument, isRequired: checked })}
                  />
                  <label htmlFor="isRequired" className="text-sm">{t('provider.documents.modals.requiredCheckbox')}</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="requiresSignature"
                    checked={newDocument.requiresSignature}
                    onCheckedChange={(checked) => setNewDocument({ ...newDocument, requiresSignature: checked })}
                  />
                  <label htmlFor="requiresSignature" className="text-sm">{t('provider.documents.modals.signatureCheckbox')}</label>
                </div>
              </div>
              
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">{t('provider.documents.modals.dropzoneMain')}</p>
                <p className="text-xs text-slate-400">{t('provider.documents.modals.dropzonePdf')}</p>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                  {t('provider.documents.modals.cancel')}
                </Button>
                <Button onClick={handleAddDocument} className="flex-1">
                  {t('provider.documents.modals.addBtn')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Upload Version Modal */}
      {showVersionModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('provider.documents.modals.uploadVersionTitle')}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowVersionModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium">{selectedDocument.title}</p>
                <p className="text-sm text-slate-500">{t('provider.documents.modals.currentVersion')} v{selectedDocument.currentVersion}</p>
              </div>
              
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-600">{t('provider.documents.modals.uploadNewVersion')}</p>
              </div>
              
              <div>
                <Label>{t('provider.documents.modals.versionNotes')}</Label>
                <textarea
                  className="w-full min-h-[80px] p-3 border rounded-lg resize-none"
                  value={versionNotes}
                  onChange={(e) => setVersionNotes(e.target.value)}
                  placeholder={t('provider.documents.modals.versionNotesPlaceholder')}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowVersionModal(false)} className="flex-1">
                  {t('provider.documents.modals.cancel')}
                </Button>
                <Button onClick={handleUploadVersion} className="flex-1">
                  {t('provider.documents.modals.uploadBtn')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Version History Modal */}
      {showHistoryModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between flex-shrink-0">
              <CardTitle>{t('provider.documents.modals.historyTitle')}</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHistoryModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </CardHeader>
            <CardContent className="overflow-y-auto">
              <p className="font-medium mb-4">{selectedDocument.title}</p>
              <div className="space-y-3">
                {selectedDocument.versions?.map((version, index) => (
                  <div key={version.version} className={`p-4 rounded-lg border ${
                    index === 0 ? 'bg-blue-50 border-blue-200' : 'bg-white'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant={index === 0 ? 'default' : 'outline'}>
                        v{version.version}
                        {index === 0 && t('provider.documents.modals.currentSuffix')}
                      </Badge>
                      <span className="text-sm text-slate-500">{version.uploadDate}</span>
                    </div>
                    <p className="text-sm text-slate-600">{version.notes}</p>
                    <p className="text-xs text-slate-400 mt-1">{t('provider.documents.modals.uploadedBy', { name: version.uploadedBy })}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedDocument && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl h-[80vh] overflow-hidden flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between flex-shrink-0">
              <CardTitle>{selectedDocument.title}</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  {t('provider.documents.modals.download')}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setShowPreviewModal(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto bg-slate-100 p-6">
              <div className="bg-white rounded-lg shadow-lg max-w-3xl mx-auto min-h-full p-8">
                <h1 className="text-2xl font-bold text-center mb-6">{selectedDocument.title}</h1>
                <p className="text-slate-600 mb-4">{selectedDocument.description}</p>
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-500 italic">{t('provider.documents.modals.previewPlaceholder')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DocumentManagement;
